import ContractStatus from './ContractStatus';
import CustomerNameEdit from '../common/CustomerNameEdit';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Col, Collapse, Dropdown, Form, Row } from 'react-bootstrap';
import { Calendar, Check2, ChevronDown, ChevronUp, Funnel, JournalText, Search, Telephone } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { ENDPOINTS } from '../../helpers/constants';
import { boardRequest, postToday } from './helpers';
import LoadMore from './LoadMore';
import { CardSkeletonGrid, LoadingStatus, MetricSkeleton, TodayCardSkeleton } from './Skeletons';

const filters = [
  ['all', 'All', 'Filter'],
  ['overdue', 'Overdue', 'Overdue'],
  ['due', 'Due today', 'Due today'],
  ['unscheduled', 'No follow-up', 'No follow-up']
];

function Metric({ value, label, tone }) {
  return (
    <Col xs={4}>
      <div className="bg-white border rounded p-2 text-center">
        <div className={`h4 mb-0 font-weight-bold ${tone}`}>{value}</div>
        <div className="text-muted small">{label}</div>
      </div>
    </Col>
  );
}

function LeadCard({ lead, onOpen, onReached, onNote, onComplete }) {
  const overdue = lead.bucket === 'overdue';
  const due = lead.bucket === 'due';
  const contracted = Boolean(lead.exported);
  return (
    <article
      className={`today-card today-row card shadow-sm ${contracted ? 'today-card--contracted' : ''} ${
        overdue && !contracted ? 'border-danger' : ''
      }`}
    >
      <div
        className={`today-card-accent ${
          contracted ? 'today-card-accent--contracted' : overdue ? 'bg-danger' : due ? 'bg-primary' : 'bg-secondary'
        }`}
      />
      <div className="card-body" role="button" onClick={onOpen}>
        <div className="d-flex">
          <span className="today-priority mr-2">{lead.priority}</span>
          <div className="min-width-0">
            <div className="d-flex flex-wrap align-items-center">
              <span className="font-weight-bold text-dark">{lead.buyer}</span><CustomerNameEdit record={lead}/>
              <Badge variant="light" className="ml-2 border">
                {lead.status}
              </Badge>
              <ContractStatus summary={lead.contractSummary} exported={contracted} />
            </div>
            <div
              className={`small font-weight-bold mt-1 ${
                contracted ? 'text-muted' : overdue ? 'text-danger' : 'text-primary'
              }`}
            >
              {lead.reason}
            </div>
            <div className="text-muted small mt-2">Last contact: {lead.lastContactLabel}</div>
          </div>
        </div>
      </div>
      <div className="today-actions d-flex border-top" onClick={event => event.stopPropagation()}>
        <Button
          as={lead.cellPhone ? 'a' : 'button'}
          href={lead.cellPhone ? `tel:${lead.cellPhone}` : undefined}
          variant="link"
          className={`flex-fill ${lead.cellPhone ? 'text-dark' : 'text-muted'}`}
          disabled={!lead.cellPhone}
        >
          <Telephone className="mr-1" /> Call
        </Button>
        <Button variant="link" className="flex-fill text-dark" onClick={onReached}>
          <Check2 className="mr-1" /> Reached
        </Button>
        <Button variant="link" className="flex-fill text-dark" onClick={onNote}>
          <JournalText className="mr-1" /> Note
        </Button>
        <Button variant="success" className="flex-fill rounded-0" onClick={onComplete}>
          <Calendar className="mr-1" /> Complete
        </Button>
      </div>
    </article>
  );
}

function ContractedContacts({ count, leads, open, onToggle, hasMore, loading, onLoad, renderLead }) {
  if (!count) return null;
  return (
    <div className="mb-4">
      <h3 className="h6 text-primary d-flex align-items-center mb-3" style={{ cursor: 'pointer' }} onClick={onToggle}>
        {open ? <ChevronUp className="mr-1" /> : <ChevronDown className="mr-1" />}
        {open ? 'Hide' : 'Show'} Contracted Contacts
        <span className="ml-1">({count})</span>
      </h3>
      <Collapse in={open}>
        <div>
          {loading && leads.length === 0 && <LoadingStatus label="Loading contracted contacts" />}
          <Row>{leads.map(renderLead)}</Row>
          {open && <LoadMore hasMore={hasMore} loading={loading} onLoad={onLoad} />}
        </div>
      </Collapse>
    </div>
  );
}

function TodayTab({ scopeQuery, reloadKey = 0, onOpen, onReload, onNote, onComplete }) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [bucket, setBucket] = useState('all');
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [contractedOpen, setContractedOpen] = useState(false);
  const [loadingContracted, setLoadingContracted] = useState(false);
  const requestId = useRef(0);
  const contractedRequestId = useRef(0);
  const contractedOpenRef = useRef(false);
  const leads = (queue?.results || []).filter(lead => !lead.exported);
  const contractedLeads = queue?.contractedResults || [];
  const counts = queue?.metrics || queue?.counts || { overdue: 0, due: 0, unscheduled: 0 };

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(
    async (page, append) => {
      const id = ++requestId.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      const response = await boardRequest(ENDPOINTS.CONTACTS_TODAY, scopeQuery, {
        page,
        search,
        bucket,
        fresh: !append && reloadKey ? '1' : '0'
      });
      if (id !== requestId.current) return;
      setLoading(false);
      setLoadingMore(false);
      if (!response) return;
      setQueue(current =>
        append && current
          ? {
              ...response,
              results: [...current.results, ...response.results],
              contractedResults: current.contractedResults || [],
              contractedPage: current.contractedPage,
              contractedHasMore: current.contractedHasMore
            }
          : { ...response, contractedResults: [] }
      );
      if (!append && contractedOpenRef.current) await loadContractedRef.current(1, false);
    },
    [scopeQuery, search, bucket, reloadKey]
  );
  const loadContractedRef = useRef(async () => {});
  contractedOpenRef.current = contractedOpen;

  const loadContracted = useCallback(
    async (page, append) => {
      const id = ++contractedRequestId.current;
      setLoadingContracted(true);
      const response = await boardRequest(ENDPOINTS.CONTACTS_TODAY, scopeQuery, {
        page: 1,
        contractedPage: page,
        search,
        bucket,
        contracted: '1',
        fresh: !append && reloadKey ? '1' : '0'
      });
      if (id !== contractedRequestId.current) return;
      setLoadingContracted(false);
      if (!response) return;
      setQueue(current => ({
        ...(current || response),
        contractedCount: response.contractedCount,
        contractedPage: response.contractedPage,
        contractedHasMore: response.contractedHasMore,
        contractedResults: append
          ? [...(current?.contractedResults || []), ...(response.contractedResults || [])]
          : response.contractedResults || []
      }));
    },
    [scopeQuery, search, bucket, reloadKey]
  );
  loadContractedRef.current = loadContracted;

  useEffect(() => {
    load(1, false);
  }, [load]);

  useEffect(() => {
    if (contractedOpen) loadContractedRef.current(1, false);
  }, [contractedOpen]);

  const loadMore = () => {
    if (!queue?.hasMore || loading || loadingMore) return;
    load((queue.page || 1) + 1, true);
  };

  const loadMoreContracted = () => {
    if (!queue?.contractedHasMore || loadingContracted) return;
    loadContracted((queue.contractedPage || 1) + 1, true);
  };

  const renderLead = lead => (
    <Col key={lead._id} md={6} xl={4} className="mb-3">
      <LeadCard
        lead={lead}
        onOpen={() => onOpen(lead)}
        onReached={async () => {
          if (!(await postToday(ENDPOINTS.CONTACTS_TODAY_REACHED(lead._id), {}))) return;
          toast.success(`Last contact set to today for ${lead.buyer}`);
          onReload();
        }}
        onNote={() => onNote(lead)}
        onComplete={() => onComplete(lead)}
      />
    </Col>
  );

  return (
    <>
      <Row className="align-items-end mb-4">
        <Col lg={8} className="mb-3 mb-lg-0">
          <div className="text-primary small font-weight-bold mb-1">YOUR WORK QUEUE</div>
          <h2 className="font-weight-bold mb-2">Focus on who needs you now.</h2>
        </Col>
        <Col lg={4}>
          <Row>
            {loading && !queue ? (
              <>
                <MetricSkeleton />
                <MetricSkeleton />
                <MetricSkeleton />
              </>
            ) : (
              <>
                <Metric value={counts.overdue} label="Overdue" tone="text-danger" />
                <Metric value={counts.due} label="Due today" tone="text-primary" />
                <Metric value={counts.unscheduled} label="No follow-up" tone="text-dark" />
              </>
            )}
          </Row>
        </Col>
      </Row>

      <div className="d-flex mb-3">
        <div className="flex-fill d-flex align-items-center border bg-white rounded px-3 mr-2">
          <Search className="text-muted mr-2" />
          <Form.Control
            className="border-0 px-0 shadow-none"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder="Search customers"
          />
        </div>
        <Dropdown>
          <Dropdown.Toggle variant="outline-dark" id="today-filter">
            <Funnel className="mr-1" />
            {filters.find(([key]) => key === bucket)?.[2]}
          </Dropdown.Toggle>
          <Dropdown.Menu alignRight>
            {filters.map(([key, label]) => (
              <Dropdown.Item key={key} active={bucket === key} onClick={() => setBucket(key)}>
                {label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {!loading && (
        <ContractedContacts
          count={queue?.contractedCount || 0}
          leads={contractedLeads}
          open={contractedOpen}
          onToggle={() => setContractedOpen(open => !open)}
          hasMore={queue?.contractedHasMore}
          loading={loadingContracted}
          onLoad={loadMoreContracted}
          renderLead={renderLead}
        />
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="h5 font-weight-bold mb-0">Do this next</h3>
        <span className="text-muted small">Ranked by urgency</span>
      </div>

      {loading && (
        <>
          <LoadingStatus label="Loading today’s queue" />
          <CardSkeletonGrid count={6} Card={TodayCardSkeleton} />
        </>
      )}
      {!loading && leads.length === 0 && (
        <div className="border bg-white rounded p-4 text-center text-muted mb-4">
          {queue?.contractedCount ? 'No active customers match this queue.' : 'No customers match this queue.'}
        </div>
      )}
      {!loading && <Row>{leads.map(renderLead)}</Row>}
      {!loading && <LoadMore hasMore={queue?.hasMore} loading={loadingMore} onLoad={loadMore} />}
    </>
  );
}

export default TodayTab;
